/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChildren,
  effect,
  ElementRef,
  forwardRef,
  HostListener,
  input,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { filter } from 'rxjs';

import { deepEqual } from '@smw/common-utils';
import { OutsideClickListener } from '@smw/utils-front';

import { SelectOptionComponent } from './option/option.component';
import { Icon, IconComponent } from '../../icon';

type Value<T> = T | T[keyof T];
export type SelectOption<T> = {
  label: string;
  value: T;
};

@Component({
  selector: 'smw-select',
  templateUrl: 'select.component.html',
  styleUrls: ['select.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectComponent),
      multi: true,
    },
  ],
  imports: [TranslateModule, IconComponent],
})
export class SelectComponent<T = unknown> implements ControlValueAccessor {
  constructor(elementRef: ElementRef, outsideClickListener: OutsideClickListener) {
    outsideClickListener
      .clickedOutsideOf(elementRef.nativeElement)
      .pipe(
        takeUntilDestroyed(),
        filter(() => this.displayOptions()),
      )
      .subscribe(() => {
        this.displayOptions.set(false);
      });
  }

  placeholder = input<string>('select_placeholder');

  multiple = input(false);
  invalid = input(false);
  trackBy = input<keyof T | undefined>(undefined);

  isDisabled = signal(false);

  protected carretIcon = Icon.carretDown;

  private optionRefs = contentChildren(SelectOptionComponent<T>, { descendants: true });
  private registerOptions = effect(() => {
    this.optionRefs().forEach((option) => option.register(this));
  });
  private options = computed<SelectOption<T>[]>(() => {
    const refs = this.optionRefs();
    if (refs.some((item) => !item.isReady())) {
      return [];
    }

    return refs.map((ref) => ref.option());
  });

  /**
   * Set when a value is provided but no options are available yet to match it against.
   * Cleared as soon as the pendingValue is set a selectedOptions
   **/
  private pendingValue = signal<Value<T> | Value<T>[] | undefined>(undefined);
  /**
   * Try to set the pending value when receiving new options
   */
  private updateValue = effect(() => {
    if (this.options().length > 0 && this.pendingValue()) {
      this.writeValue(this.pendingValue());
    }
  });

  protected displayOptions = signal<boolean>(false);
  protected selectedOptions = signal<SelectOption<T>[]>([]);
  protected isEmpty = computed(() => !this.selectedOptions().length);

  onChange = (_: Value<T> | Value<T>[]) => {};
  onTouched = () => {};

  writeValue(value: Value<T> | Value<T>[] | undefined): void {
    if (!value) {
      this.selectedOptions.set([]);
      return;
    }

    const options = this.options();
    if (!options.length) {
      this.pendingValue.set(value);
      return;
    }

    const trackBy = this.trackBy();

    const selectedOptions: SelectOption<T>[] = [];
    const select = (value: Value<T>) => {
      const item = options.find((option: SelectOption<T>) => {
        // Don't compare based on a T[key]
        if (!trackBy) {
          return deepEqual(option.value, value);
        }

        // Item and value are T
        if (
          typeof value === 'object' &&
          typeof option.value === 'object' &&
          (value as T)[trackBy] !== undefined
        ) {
          return deepEqual((option.value as T)[trackBy], (value as T)[trackBy]);
        }

        // Item is T and value is T[keyof T]
        return deepEqual((option.value as T)[trackBy], value);
      });
      if (item) {
        selectedOptions.push(item);
      }
    };

    if (this.multiple()) {
      (value as Value<T>[]).forEach((item) => select(item));
    } else {
      select(value as Value<T>);
    }

    if (selectedOptions.length > 0) {
      this.pendingValue.set(undefined);
    }

    this.selectedOptions.set(selectedOptions);
  }

  registerOnChange(fn: (_: Value<T> | Value<T>[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  selectOption(selectedOption: SelectOption<T>): void {
    const trackBy = this.trackBy();

    if (this.multiple()) {
      this.selectedOptions.update((options) => {
        const index = options.findIndex((option) => {
          if (!trackBy) {
            return deepEqual(option.value, selectedOption.value);
          }

          return deepEqual((option.value as T)[trackBy], (selectedOption.value as T)[trackBy]);
        });
        if (index === -1) {
          options.push(selectedOption);
        } else {
          options.splice(index, 1);
        }

        return [...options];
      });
      this.onChange(this.selectedOptions().map((item) => item.value));
    } else {
      this.selectedOptions.set([selectedOption]);
      this.onChange(selectedOption.value);
      this.displayOptions.set(false);
    }
  }

  isSelected(value: T): boolean {
    return this.selectedOptions().some((item) => deepEqual(item.value, value));
  }

  @HostListener('click', ['$event']) private onClick(event: MouseEvent): void {
    if (this.displayOptions()) {
      return;
    }

    this.displayOptions.set(true);
    event?.stopPropagation();
  }
}
